// ClientesListXL - Sprint 44 Fase 3 Vista 2.
// Lista de clientes XL. Reemplaza src/modules/clientes/list/ClientesList.jsx.
// Usa XLTable, XLInput (busqueda con debounce), XLSelect (pais/estado),
// XLBadge, XLChip (acciones fila).
// Agrega pager real (Anterior/Siguiente + "Pagina X de Y - N resultados").
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useClientesListXL from './hooks/useClientesListXL';
import XLTable from '../../components/xl/XLTable';
import XLInput from '../../components/xl/XLInput';
import XLSelect from '../../components/xl/XLSelect';
import XLBadge from '../../components/xl/XLBadge';
import XLChip from '../../components/xl/XLChip';
import { PAISES } from '../clientes/form/utils';

const CSS_ID = 'xl-cli-list-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
}
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__subtitle { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }

.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
  transition: background 120ms cubic-bezier(0,0,0.2,1),
              border-color 120ms cubic-bezier(0,0,0.2,1);
}
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn--ghost {
  background: transparent; color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }

.xl-filters {
  display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 12px;
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
}
@media (max-width: 720px) { .xl-filters { grid-template-columns: 1fr } }

.xl-banner {
  padding: 10px 14px; margin-bottom: 12px;
  border-radius: var(--xl-radius-md); font-size: 13px;
}
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-pager {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 12px 4px; margin-top: 12px;
  color: var(--xl-color-text-secondary); font-size: 13px;
}
.xl-pager__group { display: flex; gap: 8px }

.xl-muted { color: var(--xl-color-text-muted) }
.xl-mono  { font-family: var(--xl-font-mono); font-size: 12px }
.xl-cli-row-actions { display: flex; gap: 6px; justify-content: flex-end }
`;

const FILTRO_ESTADOS = [
  { value: 'true',  label: 'Solo activos' },
  { value: 'false', label: 'Solo inactivos' },
  { value: '',      label: 'Todos' },
];
const PAIS_OPTIONS = [
  { value: '', label: 'Todos los paises' },
  ...PAISES.map((p) => ({ value: p.name, label: p.name })),
];

function useDebouncedSetter(setter, delay) {
  const [local, setLocal] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setter(local), delay);
    return () => clearTimeout(t);
  }, [local, delay, setter]);
  return [local, setLocal];
}

export default function ClientesListXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const navigate = useNavigate();
  const cx = useClientesListXL();
  const [searchLocal, setSearchLocal] = useDebouncedSetter(cx.setQ, 300);

  const columns = useMemo(() => ([
    { key: 'nombre', label: 'Nombre',
      render: (r) => <span style={{ fontWeight: 500 }}>{r.nombre}</span> },
    { key: 'nit', label: 'NIT / Doc',
      render: (r) => <span className="xl-mono">{r.nit || '\u2014'}</span> },
    { key: 'pais', label: 'Pais',
      render: (r) => r.pais || '\u2014' },
    { key: 'ciudad', label: 'Ciudad',
      render: (r) => r.ciudad || '\u2014' },
    { key: 'email', label: 'Email',
      render: (r) => <span className="xl-mono">{r.email || '\u2014'}</span> },
    { key: 'tipo_cliente', label: 'Tipo',
      render: (r) => (
        <XLBadge variant={r.tipo_cliente === 'credito' ? 'info' : 'neutral'}>
          {r.tipo_cliente || '\u2014'}
        </XLBadge>
      ) },
    { key: 'is_active', label: 'Estado',
      render: (r) => (
        <XLBadge variant={r.is_active ? 'success' : 'neutral'} dot>
          {r.is_active ? 'Activo' : 'Inactivo'}
        </XLBadge>
      ) },
    { key: '_acc', label: '', align: 'right',
      render: (r) => (
        <span
          className="xl-cli-row-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <XLChip onClick={() => navigate('/clientes/' + r.id)}>Editar</XLChip>
          {r.is_active
            ? <XLChip variant="danger"  onClick={() => cx.deactivate(r.id)}>Inactivar</XLChip>
            : <XLChip variant="success" onClick={() => cx.activate(r.id)}>Activar</XLChip>}
        </span>
      ) },
  ]), [cx, navigate]);

  const { totalPages, canPrev, canNext, page, total, loading } = cx;

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Clientes</h2>
          <p className="xl-page__subtitle">Base de clientes global. Multi-tenant activa.</p>
        </div>
        <div>
          <button type="button" className="xl-btn xl-btn--primary"
            onClick={() => navigate('/clientes/nuevo')}>
            + Nuevo cliente
          </button>
        </div>
      </header>

      <section className="xl-filters" aria-label="Filtros de clientes">
        <XLInput
          label="Buscar"
          type="search"
          placeholder="Nombre, email o NIT..."
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)}
        />
        <XLSelect
          label="Pais"
          value={cx.pais}
          onChange={(e) => cx.setPais(e.target.value)}
          options={PAIS_OPTIONS}
        />
        <XLSelect
          label="Estado"
          value={cx.activo}
          onChange={(e) => cx.setActivo(e.target.value)}
          options={FILTRO_ESTADOS}
        />
      </section>

      {cx.msg && (
        <div
          className={'xl-banner ' + (cx.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={cx.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={cx.clearMsg}
        >
          {cx.msg.text}
        </div>
      )}
      {cx.err && (
        <div className="xl-banner xl-banner--err" role="alert">{cx.err}</div>
      )}

      <XLTable
        ariaLabel="Lista de clientes"
        columns={columns}
        data={cx.items}
        loading={loading}
        onRowClick={(r) => navigate('/clientes/' + r.id)}
        rowKey="id"
        emptyMessage="Sin clientes para estos filtros."
      />

      <div className="xl-pager">
        <span>
          {total
            ? ('Pagina ' + page + ' de ' + totalPages + ' \u00b7 ' + total + ' resultados')
            : 'Sin resultados'}
        </span>
        <span className="xl-pager__group">
          <button type="button" className="xl-btn xl-btn--ghost"
            disabled={!canPrev || loading}
            onClick={() => cx.setPage(page - 1)}>
            Anterior
          </button>
          <span className="xl-muted xl-mono">
            {page} / {totalPages}
          </span>
          <button type="button" className="xl-btn xl-btn--ghost"
            disabled={!canNext || loading}
            onClick={() => cx.setPage(page + 1)}>
            Siguiente
          </button>
        </span>
      </div>
    </div>
  );
}
